import { Link } from "react-router-dom";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const services = [
  {
    num: "01",
    title: "Foundation Systems",
    desc: "We build the core infrastructure your business runs on—ensuring every lead, customer, and workflow is captured, structured, and organized from day one.",
    items: ["CRM architecture", "Pipeline design", "Lead capture systems", "Data structuring"],
    img: "https://public.readdy.ai/ai/img_res/24ca4303ad420f79128b4d332a72bc69.jpg",
  },
  {
    num: "02",
    title: "Workflow Structuring",
    desc: "Eliminate manual work and inconsistency by designing automated workflows that handle follow-ups, scheduling, and task management end-to-end.",
    items: ["Automated follow-ups", "Smart routing", "Appointment systems", "Internal workflows"],
    img: "https://readdy.ai/api/search-image?query=abstract%20data%20flow%20animation%20freeze%20frame%20showing%20streams%20of%20light%20particles%20moving%20through%20dark%20space%20in%20perfectly%20ordered%20paths%2C%20representing%20automated%20workflow%20and%20intelligent%20routing%2C%20emerald%20green%20and%20white%20light%20trails%20on%20deep%20black%2C%20cinematic%20digital%20visualization&width=1200&height=700&seq=svc_work_01&orientation=landscape",
  },
  {
    num: "03",
    title: "Scale Infrastructure",
    desc: "Layer advanced AI systems into your operations to enable scalable growth without adding operational complexity—intelligence that runs invisibly.",
    items: ["AI agents", "Reporting dashboards", "Performance tracking", "Continuous optimization"],
    img: "https://readdy.ai/api/search-image?query=towering%20modern%20data%20center%20server%20stacks%20in%20dramatic%20dark%20environment%20illuminated%20by%20green%20LED%20accent%20lighting%2C%20representing%20enterprise%20scale%20infrastructure%20and%20computational%20power%2C%20cinematic%20industrial%20photography%20with%20dark%20atmosphere%20and%20technological%20precision&width=1200&height=700&seq=svc_scale_01&orientation=landscape",
  },
];

export default function ServicesOverviewSection() {
  const { ref: headRef, visible: headVisible } = useScrollReveal(0.1);

  return (
    <section className="w-full bg-[#050507] py-36 px-6">
      {/* Header */}
      <div
        ref={headRef as React.RefObject<HTMLDivElement>}
        className="max-w-7xl mx-auto text-center mb-28"
        style={{
          opacity: headVisible ? 1 : 0,
          transform: headVisible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
        }}
      >
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#4ADE80]/80 mb-5 block">
          Services
        </span>
        <h2
          className="font-extrabold text-white leading-[0.95] tracking-[-0.04em] mb-6"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(2.8rem, 5vw, 5rem)",
          }}
        >
          Built in Layers. Designed for Scale.
        </h2>
        <p className="text-white/40 text-lg max-w-xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
          Each layer builds upon the last—creating an interconnected system of operational intelligence.
        </p>
      </div>

      {/* Stacked service rows */}
      <div className="max-w-7xl mx-auto space-y-6">
        {services.map((svc, i) => (
          <ServiceRow key={svc.num} svc={svc} reverse={i % 2 === 1} delay={0} />
        ))}
      </div>

      {/* CTA */}
      <div className="max-w-7xl mx-auto text-center mt-20">
        <Link
          to="/services"
          className="whitespace-nowrap inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/15 text-white/70 text-sm font-medium hover:border-[#4ADE80]/40 hover:text-white transition-all duration-200 cursor-pointer"
        >
          Explore All Services <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </section>
  );
}

function ServiceRow({
  svc,
  reverse,
}: {
  svc: { num: string; title: string; desc: string; items: string[]; img: string };
  reverse: boolean;
  delay: number;
}) {
  const { ref, visible } = useScrollReveal(0.1);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`grid grid-cols-1 md:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-white/[0.06] ${
        reverse ? "md:grid-flow-dense" : ""
      }`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
        transition: "opacity 1s ease, transform 1s ease",
      }}
    >
      {/* Image */}
      <div className={`relative overflow-hidden ${reverse ? "md:order-2" : ""}`} style={{ height: "400px" }}>
        <img
          src={svc.img}
          alt={svc.title}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
        />
        <div
          className={`absolute inset-0 ${
            reverse
              ? "bg-gradient-to-l from-[#0D0D12] via-[#0D0D12]/40 to-transparent"
              : "bg-gradient-to-r from-[#0D0D12] via-[#0D0D12]/40 to-transparent"
          }`}
        />
      </div>

      {/* Content */}
      <div
        className={`bg-[#0D0D12] p-12 flex flex-col justify-center ${reverse ? "md:order-1" : ""}`}
      >
        <span
          className="text-5xl font-black text-white/[0.06] leading-none mb-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {svc.num}
        </span>
        <h3
          className="text-3xl font-bold text-white tracking-[-0.03em] mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {svc.title}
        </h3>
        <p className="text-white/45 leading-relaxed mb-8 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
          {svc.desc}
        </p>
        <ul className="space-y-2.5 mb-8">
          {svc.items.map((item) => (
            <li key={item} className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] flex-shrink-0" />
              <span className="text-sm text-white/50">{item}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/services"
          className="whitespace-nowrap inline-flex items-center gap-2 text-sm font-semibold text-[#4ADE80] hover:gap-4 transition-all duration-200 cursor-pointer"
        >
          Learn More <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </div>
  );
}
